import { DocumentArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useRef, useState, useEffect } from "react";
import Compressor from 'compressorjs';
import { useTranslation } from "react-i18next";
import LoadingWheel from "../LoadingWheel";

const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 10485760;
const KILO_BYTES_PER_BYTE = 1024;
const MAX_PARALLEL_IMAGE_COMPRESSIONS = 3;

const convertBytesToKB = (bytes) => Math.round(bytes / KILO_BYTES_PER_BYTE);

export default function FileUpload({
  label,
  updateFilesCb,
  maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
  driver = false,
  droppedFiles = [],
  ...otherProps
}) {
  const { t } = useTranslation("common");
  const fileInputField = useRef(null);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleUploadBtnClick = () => {
    fileInputField.current.click();
  };

  // const addNewFiles = (newFiles) => {
  //   return new Promise((resolve) => {
  //     const files = {};
  //     console.log("add new files", newFiles)
  //     //Array of promises to resolve before returning
  //     const compressPromises = [];
  //     for (let file of newFiles) {
  //       if (file.size <= maxFileSizeInBytes) {
  //         compressPromises.push(
  //           new Promise((resolve) => {
  //             new Compressor(file, {
  //               quality: 0.7,
  //               success: (compressedFile) => {
  //                 //Change blob back to file
  //                 const newFile = new File([compressedFile], file.name, { type: file.type });

  //                 //Check if multiple files are allowed
  //                 if (!otherProps.multiple) {
  //                   resolve({ newFile });
  //                 } else {
  //                   files[file.name] = newFile;
  //                   resolve();
  //                 }
  //               },
  //             });
  //           })
  //         );
  //       }
  //     }

  //     Promise.all(compressPromises).then(() => {
  //       resolve({ ...files });
  //     });
  //   });
  // };

  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.7,
        success(blob) {
          const compressedFile = new File([blob], file.name, { type: file.type });
          resolve(compressedFile);
        },
        error(err) {
          reject(err);
        }
      });
    });

  const addNewFiles = async (fileList) => {
    const filesArr = Array.from(fileList).filter(file => file.size <= maxFileSizeInBytes);
    if (!filesArr.length) return [];

    const compressed = new Array(filesArr.length);
    let nextFileIndex = 0;

    const worker = async () => {
      while (nextFileIndex < filesArr.length) {
        const currentIndex = nextFileIndex++;
        const file = filesArr[currentIndex];

        if (!file.type.startsWith("image/")) {
          compressed[currentIndex] = file;
          continue;
        }

        try {
          compressed[currentIndex] = await compressImage(file);
        } catch {
          compressed[currentIndex] = file;
        }
      }
    };

    const workerCount = Math.min(MAX_PARALLEL_IMAGE_COMPRESSIONS, filesArr.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    return compressed.filter(Boolean);
  };

  const handleNewFileUpload = async (e) => {
    const rawFiles = e.target.files;
    if (!rawFiles || !rawFiles.length) return;

    setIsCompressing(true);
    try {
      // get back an Array<File>
      const compressedFiles = await addNewFiles(rawFiles);

      setFiles(compressedFiles);
      callUpdateFilesCb(compressedFiles);
    } finally {
      setIsCompressing(false);
    }

    // reset input
    e.target.value = "";
  };


  const callUpdateFilesCb = (files) => {
    updateFilesCb(files);
  };

  // const handleNewFileUpload = async (e) => {
  //   const newFiles = Array.from(e.target.files);

  //   if (newFiles.length) {
  //     let updatedFiles = await addNewFiles(newFiles);

  //     setFiles(updatedFiles);
  //     callUpdateFilesCb(updatedFiles);
  //   }
  // };

  const removeFile = (fileIndex) => {
    const updatedFiles = files.filter((_, index) => index !== fileIndex);
    setFiles(updatedFiles);
    callUpdateFilesCb(updatedFiles);
  };

  // whenever parent drops come in, compress & add them
  useEffect(() => {
    if (droppedFiles.length) {
      (async () => {
        setIsCompressing(true);
        try {
          const updated = await addNewFiles(droppedFiles);
          setFiles(updated);
          callUpdateFilesCb(updated);
        } finally {
          setIsCompressing(false);
        }
      })();
    }
  }, [droppedFiles]);

  useEffect(() => {
    const urls = files.map(file => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [files]);

  return (
    <>
      <section className="flex flex-col items-center bg-white relative mt-8 mb-4 py-[35px] px-[20px] border border-gray-400 border-dotted">
        <label className="-top-7 text-lg font-semibold text-black left-0 absolute">
          {label}
          {otherProps.desc && <span className="text-gray-500 text-sm ml-2">- {otherProps.desc}</span>}
        </label>

        {!driver && (<p className="font-bold tracking-widest mt-0 text-center">{t("Drag and drop your files anywhere or")}</p>)}
        <button
          type="button"
          className="mt-2 box-border appearance-none bg-transparent border border-[#3498db] text-xl cursor-pointer leading-none py-[1.1em] px-[2.8em] text-center uppercase font-bold rounded-[6px] text-[#3498db] relative overflow-hidden z-10 flex items-center w-[45%] justify-center hover:bg-[#3498db] hover:text-white"
          onClick={handleUploadBtnClick}
          disabled={isCompressing}
        >
          <DocumentArrowUpIcon className="w-[22px] h-[22px] mr-1 flex flex-col justify-center" />
          <span> {otherProps.multiple ? t("Upload files") : t("Upload a file")}</span>
        </button>
        {isCompressing && (
          <div className="mt-4 flex flex-col items-center">
            <LoadingWheel />
            <span className="mt-2 text-sm text-gray-600">{t("Processing files...")}</span>
          </div>
        )}
        <input
          className="font-lg block w-full border-none normal-case absolute top-0 left-0 right-0 bottom-0 opacity-0 focus:outline-none"
          type="file"
          ref={fileInputField}
          title=""
          value=""
          onChange={handleNewFileUpload}
          {...otherProps}
        />
      </section>

      {/*Upload previews*/}
      <article className="@container w-full !mb-12">
        {files.length > 0 && <span className="text-md">{t("Upload Preview")}</span>}
        <div className="grid gap-6 mt-4 grid-cols-2 @sm:grid-cols-3 @3xl:grid-cols-4 @4xl:grid-cols-5">
          {files.map((file, index) => {
            let isImageFile = file.type.split("/")[0] === "image";
            return (
              <div className="p-1 rounded-sm box-border mx-auto w-1/2 sm:w-full hover:opacity-60 h-full relative" key={`${file.name}-${index}`}>
                <div className="h-full flex items-center">
                  {isImageFile && (
                    <img
                      className="rounded-sm object-contain w-full h-full"
                      src={previewUrls[index]}
                      alt={t("file preview {{index}}", { index })}
                    />
                  )}
                  <div className="opacity-0 flex flex-col justify-between z-20 absolute top-0 right-0 bottom-0 left-0 p-2 rounded-[6px] text-white font-bold bg-[rgba(0,0,0,0.75)] hover:opacity-100" isImageFile={isImageFile}>
                    <span>{file.name}</span>
                    <aside className="flex justify-between">
                      <span>{convertBytesToKB(file.size)} kb</span>
                      <TrashIcon className="h-8 w-8 cursor-pointer hover:scale-125" onClick={() => removeFile(index)} />
                    </aside>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </>
  );
};